/**
 * Title / main menu + new-run setup. Entry point: Continue (resume saved run),
 * New Run (character + seed + Heat selector), Compendium, Settings.
 */

import { el, div, span, button } from "../dom";
import type { Store } from "../store";
import {
  CHARACTER_DEFS,
  getCharacterDef,
  getProgramDef,
  HEAT_DESCRIPTIONS,
  MAX_HEAT,
  type CharacterId,
} from "../../engine";

/** Local setup state (lives only while the title screen is mounted). */
interface Setup {
  mode: "menu" | "new";
  character: CharacterId;
  heat: number;
  seed: string;
}

const setup: Setup = {
  mode: "menu",
  character: "ghost",
  heat: 0,
  seed: "",
};

/** A short, friendly random seed (display only; the engine hashes any string). */
function randomSeed(): string {
  const words = [
    "NEON",
    "VOID",
    "ICE",
    "GHOST",
    "ZERO",
    "FLUX",
    "ECHO",
    "GRID",
    "HEX",
    "NULL",
  ];
  const w = words[Math.floor(Math.random() * words.length)];
  const n = Math.floor(Math.random() * 9000) + 1000;
  return `${w}-${n}`;
}

export function renderTitle(store: Store): HTMLElement {
  const screen = div("screen title-screen");

  if (setup.mode === "menu") {
    screen.appendChild(logo());
    screen.appendChild(
      span("tagline", "A DETERMINISTIC DECKBUILDER · BREACH THE ICE"),
    );

    const menu = div("menu");
    const canContinue = store.canContinue();
    menu.appendChild(
      button(
        "btn btn--primary btn--wide",
        canContinue ? "▶ CONTINUE" : "▶ CONTINUE (none)",
        () => store.continueRun(),
        { disabled: !canContinue },
      ),
    );
    menu.appendChild(
      button("btn btn--wide", "✦ NEW RUN", () => {
        setup.mode = "new";
        setup.seed = randomSeed();
        store.open("title"); // re-render
      }),
    );
    menu.appendChild(
      button("btn btn--wide", "▤ COMPENDIUM", () => store.open("compendium")),
    );
    menu.appendChild(
      button("btn btn--wide", "⚙ SETTINGS", () => store.open("settings")),
    );
    screen.appendChild(menu);

    if (store.loadIssue) {
      screen.appendChild(
        div("notice err", [
          `Saved run was unreadable (${store.loadIssue}); it was cleared.`,
        ]),
      );
    }
    screen.appendChild(
      span("muted center", "v1 · keyboard + mouse · seeds are shareable"),
    );
    return screen;
  }

  // --- NEW RUN setup ------------------------------------------------------
  screen.classList.remove("title-screen");
  screen.appendChild(el("div", { class: "h-title center", text: "NEW RUN" }));

  const body = div("screen__body scroll");
  body.style.alignItems = "center";

  // Character picker
  body.appendChild(el("div", { class: "h-sub", text: "CHOOSE RUNNER" }));
  const chars = div("row wrap");
  chars.style.justifyContent = "center";
  for (const id of Object.keys(CHARACTER_DEFS) as CharacterId[]) {
    const unlocked = store.meta.unlockedCharacters.includes(id);
    const def = getCharacterDef(id);
    const sel = setup.character === id;
    const card = div(
      `panel${sel ? " panel--raised" : ""}`,
      [
        el("div", {
          class: "h-sub",
          text: def.name + (unlocked ? "" : " 🔒"),
          style: sel ? "color:var(--amber)" : "",
        }),
        el("div", { class: "muted", text: `HP ${def.maxHp}`, style: "margin:4px 0" }),
        el("div", {
          class: "muted",
          text: def.description,
          style: "max-width:300px;white-space:normal;line-height:1.4",
        }),
      ],
    );
    card.style.width = "320px";
    card.style.cursor = unlocked ? "pointer" : "not-allowed";
    card.style.borderColor = sel ? "var(--amber)" : "";
    if (unlocked) {
      card.addEventListener("click", () => {
        setup.character = id;
        store.open("title");
      });
    } else {
      card.style.opacity = "0.5";
    }
    chars.appendChild(card);
  }
  body.appendChild(chars);

  // Seed
  body.appendChild(el("div", { class: "h-sub", text: "SEED" }));
  const seedInput = el("input", {
    type: "text",
    value: setup.seed,
    maxlength: "24",
    style:
      "font-family:inherit;font-size:14px;background:var(--bg-0);color:var(--ink-max);border:2px solid var(--bg-4);padding:6px 10px;width:240px;text-align:center;letter-spacing:2px;",
  }) as HTMLInputElement;
  seedInput.addEventListener("input", () => {
    setup.seed = seedInput.value;
  });
  const reroll = button("btn btn--sm", "⟳ RANDOM", () => {
    setup.seed = randomSeed();
    store.open("title");
  });
  body.appendChild(div("row", [seedInput, reroll]));

  // Heat selector
  body.appendChild(el("div", { class: "h-sub", text: "HEAT (ASCENSION)" }));
  const maxUnlockedHeat = Math.min(
    MAX_HEAT,
    (store.meta.ascension[setup.character] ?? 0),
  );
  const heatRow = div("row", [
    button("btn btn--sm", "◀", () => {
      setup.heat = Math.max(0, setup.heat - 1);
      store.open("title");
    }, { disabled: setup.heat <= 0 }),
    el("div", {
      class: "h-sub",
      text: `HEAT ${setup.heat}`,
      style: "width:90px;text-align:center;color:var(--red)",
    }),
    button("btn btn--sm", "▶", () => {
      setup.heat = Math.min(maxUnlockedHeat, setup.heat + 1);
      store.open("title");
    }, { disabled: setup.heat >= maxUnlockedHeat }),
  ]);
  body.appendChild(heatRow);
  body.appendChild(
    el("div", {
      class: "muted center",
      style: "max-width:380px;white-space:normal;min-height:30px",
      text:
        setup.heat === 0
          ? maxUnlockedHeat > 0
            ? "Base difficulty. Raise Heat for tougher, more interesting runs."
            : "Base difficulty. Win a run to unlock Heat tiers."
          : `Heat ${setup.heat}: ${HEAT_DESCRIPTIONS[setup.heat] ?? ""}`,
    }),
  );

  // Keystone preview
  const ks = getCharacterDef(setup.character).startingProgram;
  if (ks) {
    const k = getProgramDef(ks);
    body.appendChild(
      el("div", {
        class: "muted center",
        style: "max-width:420px;white-space:normal;margin-top:4px",
        text: `Keystone — ${k.name}: ${k.description}`,
      }),
    );
  }

  screen.appendChild(body);

  const actions = div("row");
  actions.style.justifyContent = "center";
  actions.appendChild(
    button("btn", "← BACK", () => {
      setup.mode = "menu";
      store.open("title");
    }),
  );
  actions.appendChild(
    button("btn btn--primary", "BREACH ▶", () => {
      const seed = setup.seed.trim() || randomSeed();
      setup.mode = "menu";
      store.startRun(seed, setup.character, setup.heat);
    }),
  );
  screen.appendChild(actions);
  return screen;
}

function logo(): HTMLElement {
  return el("div", {
    class: "logo",
    html: 'NULL<span class="z">RUN</span>',
  });
}

/** Reset setup to menu (called when leaving the title via a run start). */
export function resetTitleSetup(): void {
  setup.mode = "menu";
}
